/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';
import { normalize } from 'normalizr';

import { isCacheExpired } from '@ibm-watson/shared/modules/ia/store/helper';
import * as messageActions from '@ibm-watson/shared/modules/ia/store/messages/actions';
import * as operations from '@ibm-watson/shared/modules/ia/store/operations';
import { getErrorString } from '@ibm-watson/shared/modules/ia/util/errorHandler';
import {
  createdObjectEvents,
  deletedObjectEvents,
  updatedObjectEvents
} from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import * as segment from '@ibm-watson/shared/modules/ia/util/trackers/track-when-ready';
import { Intent } from '../../../../ts/action';
import { ReactivateSession } from '../../../../ts/session';
import { AppDispatch, CreateAsyncThunk } from '../../../../ts/state';
import { selectIsAccessUpdatesEnabled } from '../../../ia/store/features/selectors';
import { selectIsNewIa } from '../../../ia/store/instance/selectors';
import { getAssistantId } from '../../../ia/store/session/selectors';
import * as counterExampleApi from '../../api/counter-examples';
import * as exampleApi from '../../api/examples';
import * as intentsApi from '../../api/intents';
import * as actionTypes from './actionTypes';
import { intentSchema, intentsSchema } from './schema';
import { areIntentsLoading, getIntents } from './selectors';

const buildModelObject = (operation, items) => ({
  payload: { operation },
  response: {
    entities: {
      intents: items
    }
  }
});

export const addIntentStart = (skillId, intent, clusterName) => ({
  type: actionTypes.ADD_INTENT_START,
  intent,
  skillId,
  clusterName
});

export const addIntentSuccess = (response, intent) => ({
  type: actionTypes.ADD_INTENT_SUCCESS,
  intent,
  response,
  payload: { operation: operations.CREATE }
});
export const addIntentError = error => ({
  type: actionTypes.ADD_INTENT_ERROR,
  transactionId: error.transactionId,
  error
});

export const addIntent =
  (skillId: string, intent: any, clusterName: string) =>
  async (dispatch, getState) => {
    dispatch(addIntentStart(skillId, intent, clusterName));
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to create
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    try {
      const newIntent = {
        ...intent,
        ...(await intentsApi.createIntent(
          skillId,
          useNewIAUpdatedAccess,
          assistantId,
          intent,
          clusterName
        ))
      };
      dispatch(addIntentSuccess(normalize(newIntent, intentSchema), newIntent));
      createdObjectEvents.intent({
        object: skillId ? `${skillId}--${intent.intent}` : intent.intent,
        resultValue: 'success',
        successFlag: true
      });
      return newIntent;
    } catch (e) {
      dispatch(addIntentError(_.get(e, 'message', e)));
      createdObjectEvents.intent({
        object: null,
        resultValue: getErrorString(e) || e,
        successFlag: false
      });
      throw e;
    }
  };

export const filterIntents = (skillId, queryString) => ({
  // AccessControl: function used in Classic only
  type: actionTypes.FILTER_INTENTS_START,
  queryString,
  skillId
});

export const filterIntentsSuccess = response => ({
  type: actionTypes.FILTER_INTENTS_SUCCESS,
  response,
  payload: { operation: operations.LIST }
});

export const filterIntentsError = error => ({
  type: actionTypes.FILTER_INTENTS_ERROR,
  error
});

export const fetchIntentsStart = skillId => ({
  type: actionTypes.FETCH_INTENTS_START,
  skillId
});

export const fetchIntentsSuccess = response => ({
  type: actionTypes.FETCH_INTENTS_SUCCESS,
  response,
  payload: { operation: operations.LIST }
});

export const fetchIntentsError = error => ({
  type: actionTypes.FETCH_INTENTS_ERROR,
  transactionId: error.transactionId,
  error
});

export const fetchIntents =
  (skillId: string, options?: any, reactivateSession?: ReactivateSession) =>
  async (dispatch, getState) => {
    dispatch(fetchIntentsStart(skillId));
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to listIntents
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());

    try {
      const response = await intentsApi.listIntents(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        options,
        reactivateSession
      );
      const normalizedResult = normalize(response.intents, intentsSchema);
      dispatch(fetchIntentsSuccess(normalizedResult));
      return {
        intents: response.intents,
        nextURL: response.nextURL,
        total: response.total
      };
    } catch (error) {
      dispatch(fetchIntentsError(error));
      throw error;
    }
  };

export const fetchIntentsIfRequired = createAsyncThunk<
  Record<string, Intent>,
  { skillId: string; options: any },
  CreateAsyncThunk
>(
  'intents/fetchIntentsIfRequired',
  async ({ skillId, options }, { dispatch, getState }) => {
    const state = getState();
    if (
      !state?.intents?.props?.lastFetched ||
      isCacheExpired(state?.intents?.props?.lastFetched)
    ) {
      await dispatch(fetchIntents(skillId, options, ReactivateSession.NEVER));
    }
    return getIntents(state);
  },
  {
    condition: (params, { getState }) => {
      const isLoading = areIntentsLoading(getState());
      return !isLoading;
    }
  }
);

export const fetchMoreIntentsStart = nextURL => ({
  type: actionTypes.FETCH_MORE_INTENTS_START,
  nextURL
});

export const fetchMoreIntentsSuccess = response => ({
  type: actionTypes.FETCH_MORE_INTENTS_SUCCESS,
  response,
  payload: { operation: operations.UPDATE }
});

export const fetchMoreIntentsError = error => ({
  type: actionTypes.FETCH_MORE_INTENTS_ERROR,
  error
});

export const fetchMoreIntents = nextURL => async dispatch => {
  dispatch(fetchMoreIntentsStart(nextURL));

  try {
    const response = await intentsApi.listMoreIntents(nextURL);
    const normalizedResult = normalize(response.intents, intentsSchema);
    dispatch(fetchMoreIntentsSuccess(normalizedResult));
    return {
      intents: response.intents,
      nextURL: response.nextURL
    };
  } catch (error) {
    dispatch(fetchMoreIntentsError(error));
    throw error;
  }
};

export const fetchIntentConflictsStart = skillId => ({
  type: actionTypes.FETCH_INTENT_CONFLICTS_START,
  skillId
});

export const fetchIntentConflictsSuccess = response => ({
  type: actionTypes.FETCH_INTENT_CONFLICTS_SUCCESS,
  response,
  payload: { operation: operations.UPDATE }
});

export const fetchIntentConflictsError = error => ({
  type: actionTypes.FETCH_INTENT_CONFLICTS_ERROR,
  error
});

export const fetchIntentConflicts =
  (skillId, intentsArray) => async dispatch => {
    dispatch(fetchIntentConflictsStart(skillId));

    try {
      const response: any = await intentsApi.getFilteredList(
        skillId,
        intentsArray
      );
      const normalizedResult = normalize(response.intents, intentsSchema);
      dispatch(fetchIntentConflictsSuccess(normalizedResult));
      return {
        intents: response.intents,
        nextURL: response.nextURL,
        total: response.total
      };
    } catch (error) {
      dispatch(fetchIntentConflictsError(error));
      throw error;
    }
  };

export const addIntents = intents => ({
  type: actionTypes.ADD_INTENTS,
  intents
});

export const removeIntentsStart = (skillId, intents) => ({
  type: actionTypes.REMOVE_INTENTS_START,
  skillId,
  intents
});
export const removeIntentsSuccess = intents => {
  return {
    type: actionTypes.REMOVE_INTENTS_SUCCESS,
    intents: intents.map(intent => intent.intent),
    ...buildModelObject(
      operations.DELETE,
      intents.reduce((accumulator, currentValue) => {
        accumulator[currentValue.intent] = currentValue;
        return accumulator;
      }, {})
    )
  };
};
export const removeIntentsError = error => ({
  type: actionTypes.REMOVE_INTENTS_ERROR,
  error
});

export const removeIntents =
  (skillId: string, intents: Intent[], deleteAll: boolean = false) =>
  async (dispatch: AppDispatch, getState) => {
    dispatch(removeIntentsStart(skillId, intents));
    let deletedIntents = intents;
    try {
      // Use revised API for Access Control improvements if enabled & isNewIA
      // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to listIntents, deleteIntent
      const assistantId = getAssistantId(getState());
      const isNewIA = selectIsNewIa(getState());
      const useNewIAUpdatedAccess =
        isNewIA && selectIsAccessUpdatesEnabled(getState());
      if (deleteAll) {
        // when deleting all intents, we need to first get all intents
        // then use all intents id and make a PATCH call to delete all

        const response = await intentsApi.listIntents(
          skillId,
          useNewIAUpdatedAccess,
          assistantId,
          {
            page_limit: 100000
          }
        );
        deletedIntents = response.intents;
      }
      if (deletedIntents.length === 1) {
        // when we only have one intent to delete,
        // we should make a call to `DELETE /intents/{intent_id}`
        // instead of making a PATCH call.
        await intentsApi.deleteIntent(
          skillId,
          useNewIAUpdatedAccess,
          assistantId,
          deletedIntents[0]
        );
      } else {
        await intentsApi.removeIntents(
          skillId,
          useNewIAUpdatedAccess,
          assistantId,
          deletedIntents
        );
      }
      dispatch(removeIntentsSuccess(deletedIntents));

      if (deletedIntents.length > 1) {
        segment.customEvent(
          'BulkDeleted',
          skillId,
          'Intents',
          true,
          null,
          'DeleteSome',
          'true'
        );
      } else {
        deletedObjectEvents.intent({
          object: skillId
            ? `${skillId}--${deletedIntents[0]?.intent}`
            : deletedIntents[0]?.intent,
          resultValue: 'success',
          successFlag: true
        });
      }

      return intents;
    } catch (e) {
      dispatch(removeIntentsError(e.message));

      if (deletedIntents.length > 1) {
        segment.customEvent(
          'BulkDeleted',
          skillId,
          'Intents',
          false,
          null,
          'DeleteSome',
          'false'
        );
      } else {
        deletedObjectEvents.intent({
          object: skillId
            ? `${skillId}--${deletedIntents[0]?.intent}`
            : deletedIntents[0]?.intent,
          resultValue: getErrorString(e) || e,
          successFlag: false
        });
      }
      throw e;
    }
  };

export const deleteAllIntentsStart = () => ({
  type: actionTypes.DELETE_ALL_INTENTS_START
});

export const deleteAllIntentsSuccess = response => ({
  type: actionTypes.DELETE_ALL_INTENTS_SUCCESS,
  response,
  payload: { operation: operations.DELETE }
});

export const deleteAllIntentsError = error => ({
  type: actionTypes.DELETE_ALL_INTENTS_ERROR,
  transactionId: error.transactionId,
  error
});

export const deleteAllIntents = skillId => async dispatch => {
  dispatch(deleteAllIntentsStart());
  try {
    await intentsApi.deleteAllIntents(skillId);
    dispatch(deleteAllIntentsSuccess([]));
    segment.customEvent(
      'BulkDeleted',
      skillId,
      'Intents',
      true,
      null,
      'DeleteAll',
      'true'
    );
    return [];
  } catch (error) {
    dispatch(deleteAllIntentsError(error));
    segment.customEvent(
      'BulkDeleted',
      skillId,
      'Intents',
      false,
      null,
      'DeleteAll',
      'false'
    );
    throw error;
  }
};

export const replaceIntentsStart = () => ({
  type: actionTypes.REPLACE_INTENTS_START
});

export const replaceIntentsSuccess = response => ({
  type: actionTypes.REPLACE_INTENTS_SUCCESS,
  response,
  payload: { operation: operations.LIST }
});

export const replaceIntentsError = error => ({
  type: actionTypes.REPLACE_INTENTS_ERROR,
  error
});

export const replaceIntents =
  (skillId, intentsToKeep) => async (dispatch, getState) => {
    dispatch(replaceIntentsStart());
    try {
      await intentsApi.replaceIntents(skillId, intentsToKeep);
      // Use revised API for Access Control improvements if enabled & isNewIA
      // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to list
      const assistantId = getAssistantId(getState());
      const isNewIA = selectIsNewIa(getState());
      const useNewIAUpdatedAccess =
        isNewIA && selectIsAccessUpdatesEnabled(getState());
      const response = await intentsApi.listIntents(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        {},
        null
      );
      const normalizedResult = normalize(response.intents, intentsSchema);
      dispatch(replaceIntentsSuccess(normalizedResult));
      segment.customEvent(
        'BulkDeleted',
        skillId,
        'Intents',
        true,
        null,
        'InvertDelete',
        'true'
      );
      return {
        intents: normalizedResult,
        nextURL: response.nextURL,
        total: response.total
      };
    } catch (error) {
      dispatch(replaceIntentsError(error));
      segment.customEvent(
        'BulkDeleted',
        skillId,
        'Intents',
        false,
        null,
        'InvertDelete',
        'false'
      );
      throw error;
    }
  };

export const editIntentStart = (skillId, currentIntentName, intent) => ({
  type: actionTypes.UPDATE_INTENT_UPDATE_START,
  currentIntentName,
  intent,
  skillId
});

export const editIntentSuccess = (response, intent) => ({
  type: actionTypes.UPDATE_INTENT_UPDATE_SUCCESS,
  intent,
  response,
  payload: { operation: operations.UPDATE }
});

export const editIntentError = error => ({
  type: actionTypes.UPDATE_INTENT_UPDATE_ERROR,
  transactionId: error.transactionId,
  error
});

export const editIntent =
  (skillId, currentIntentName, intent) => async (dispatch, getState) => {
    dispatch(editIntentStart(skillId, currentIntentName, intent));
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to getIntent
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    try {
      const newIntent = {
        ...intent,
        ...(await intentsApi.updateIntent(
          skillId,
          useNewIAUpdatedAccess,
          assistantId,
          currentIntentName,
          intent
        ))
      };
      const fetchedIntent = await intentsApi.getIntent(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        intent.intent
      );
      dispatch(
        editIntentSuccess(normalize(fetchedIntent, intentSchema), newIntent)
      );
      updatedObjectEvents.intent({
        object: skillId ? `${skillId}--${intent.intent}` : intent.intent,
        resultValue: 'success',
        successFlag: true
      });
      if (currentIntentName !== newIntent.intent) {
        dispatch(removeIntentsSuccess([{ intent: currentIntentName }]));
      }
      return fetchedIntent;
    } catch (e) {
      dispatch(editIntentError(_.get(e, 'message', e)));
      updatedObjectEvents.intent({
        object: skillId ? `${skillId}--${intent.intent}` : intent.intent,
        resultValue: getErrorString(e) || e,
        successFlag: false
      });
      throw e;
    }
  };

export const cancelEditIntent = () => ({
  type: actionTypes.UPDATE_INTENT_CANCEL
});

export const addExampleStart = (skillId, intent, example) => ({
  type: actionTypes.ADD_INTENT_EXAMPLE_START,
  skillId,
  intent,
  example
});

export const addExampleSuccess = (response, intent) => ({
  type: actionTypes.ADD_INTENT_EXAMPLE_SUCCESS,
  intent,
  response,
  payload: { operation: operations.UPDATE }
});

export const addExampleError = error => ({
  type: actionTypes.ADD_INTENT_EXAMPLE_ERROR,
  transactionId: error.transactionId,
  error
});

export const addExample =
  (skillId: string, intent: any, example: any, handleErrors?: boolean) =>
  async (dispatch, getState) => {
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to create
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    dispatch(addExampleStart(skillId, intent, example));
    try {
      const newExample = await exampleApi.createExample(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        intent.intent,
        example
      );
      let existingIntent = intent;
      if (getState().models.intents) {
        existingIntent = _.cloneDeep(getState().models.intents[intent.intent]);
      }
      const updatedIntent = {
        ...existingIntent,
        examples: [...existingIntent.examples, newExample]
      };
      updatedIntent.updated = new Date().toISOString();
      dispatch(
        addExampleSuccess(normalize(updatedIntent, intentSchema), updatedIntent)
      );
      createdObjectEvents.userExample({
        object: skillId ? `${skillId}--${example.text}` : example.text,
        resultValue: 'success',
        successFlag: true
      });
      return newExample;
    } catch (e) {
      dispatch(addExampleError(e));
      createdObjectEvents.userExample({
        object: null,
        resultValue: getErrorString(e) || e,
        successFlag: false
      });
      if (handleErrors) {
        dispatch(messageActions.addErrorMessage(e));
      }
      throw e;
    }
  };

export const addExamplesStart = (
  skillId,
  intent,
  examples,
  recommendsAction,
  headerId,
  clusterName
) => ({
  type: actionTypes.ADD_EXAMPLES_START,
  skillId,
  intent,
  examples,
  recommendsAction,
  headerId,
  clusterName
});

export const addExamplesSuccess = (response, intent) => ({
  type: actionTypes.ADD_EXAMPLES_SUCCESS,
  intent,
  response,
  payload: { operation: operations.UPDATE }
});

export const addExamplesError = error => ({
  type: actionTypes.ADD_EXAMPLES_ERROR,
  transactionId: error.transactionId,
  error
});

export const addExamples =
  (skillId, intent, examples, recommendsAction, headerId, clusterName) =>
  async dispatch => {
    dispatch(
      addExamplesStart(
        skillId,
        intent,
        examples,
        recommendsAction,
        headerId,
        clusterName
      )
    );
    try {
      const updatedIntent = await intentsApi.addExamples(
        skillId,
        intent,
        examples,
        recommendsAction,
        headerId,
        clusterName
      );
      updatedIntent.examples = [...intent.examples, ...examples];
      dispatch(
        addExamplesSuccess(
          normalize(updatedIntent, intentSchema),
          updatedIntent
        )
      );
      return updatedIntent;
    } catch (e) {
      dispatch(addExamplesError(e));
      throw e;
    }
  };

export const updateExampleStart = (skillId, intent, exampleText, example) => ({
  type: actionTypes.UPDATE_INTENT_EXAMPLE_START,
  skillId,
  intent,
  exampleText,
  example
});

export const updateExampleSuccess = (response, intent) => ({
  type: actionTypes.UPDATE_INTENT_EXAMPLE_SUCCESS,
  intent,
  response,
  payload: { operation: operations.UPDATE }
});

export const updateExampleError = error => ({
  type: actionTypes.UPDATE_INTENT_EXAMPLE_ERROR,
  transactionId: error.transactionId,
  error
});

export const updateExample =
  (skillId: string, intent: any, exampleText: string, example: any) =>
  async (dispatch, getState) => {
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to update
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    dispatch(updateExampleStart(skillId, intent, exampleText, example));
    try {
      await exampleApi.updateExample(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        intent.intent,
        exampleText,
        example
      );

      const updatedIntent = intent;
      const removeIdx = _.findIndex(
        updatedIntent.examples,
        (ex: any) => ex.text === exampleText
      );
      if (removeIdx >= 0) {
        updatedIntent.examples.splice(removeIdx, 1);
        updatedIntent.examples.push(example);
      }
      updatedIntent.updated = new Date().toISOString();

      dispatch(
        updateExampleSuccess(
          normalize(updatedIntent, intentSchema),
          updatedIntent
        )
      );
      updatedObjectEvents.userExample({
        object: skillId ? `${skillId}--${example.text}` : example.text,
        resultValue: 'success',
        successFlag: true
      });
      return updatedIntent;
    } catch (e) {
      dispatch(updateExampleError(e));
      updatedObjectEvents.userExample({
        object: skillId ? `${skillId}--${example.text}` : example.text,
        resultValue: getErrorString(e) || e,
        successFlag: false
      });
      throw e;
    }
  };

export const removeExamplesStart = (skillId, intent, examples) => ({
  type: actionTypes.REMOVE_INTENT_EXAMPLES_START,
  skillId,
  intent,
  examples
});

export const removeExamplesSuccess = (response, intent) => ({
  type: actionTypes.REMOVE_INTENT_EXAMPLES_SUCCESS,
  intent,
  response,
  payload: { operation: operations.UPDATE }
});

export const removeExamplesError = error => ({
  type: actionTypes.REMOVE_INTENT_EXAMPLES_ERROR,
  transactionId: error.transactionId,
  error
});

export const removeExamples =
  (skillId: string, intent: any, examples: any) =>
  async (dispatch, getState) => {
    dispatch(removeExamplesStart(skillId, intent, examples));
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on calls
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    try {
      await exampleApi.removeExamples(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        intent.intent,
        examples
      );
      const newIntent = await intentsApi.getIntent(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        intent.intent
      );
      dispatch(
        removeExamplesSuccess(normalize(newIntent, intentSchema), newIntent)
      );
      if (
        examples.length > 1 &&
        examples.length !== _.get(intent, 'examples.length')
      ) {
        segment.customEvent(
          'BulkDeleted',
          skillId,
          'Examples',
          true,
          null,
          'DeleteSome',
          'true'
        );
      } else if (examples.length === _.get(intent, 'examples.length')) {
        segment.customEvent(
          'BulkDeleted',
          skillId,
          'Examples',
          true,
          null,
          'DeleteAll',
          'true'
        );
      } else {
        deletedObjectEvents.userExample({
          object: `${skillId}--${examples[0]?.text}`,
          resultValue: 'success',
          successFlag: true
        });
      }
      return examples;
    } catch (e) {
      dispatch(removeExamplesError(e.message));
      if (
        examples.length > 1 &&
        examples.length !== _.get(intent, 'examples.length')
      ) {
        segment.customEvent(
          'BulkDeleted',
          skillId,
          'Examples',
          false,
          null,
          'DeleteSome',
          'false'
        );
      } else if (examples.length === _.get(intent, 'examples.length')) {
        segment.customEvent(
          'BulkDeleted',
          skillId,
          'Examples',
          false,
          null,
          'DeleteAll',
          'false'
        );
      } else {
        deletedObjectEvents.userExample({
          object: `${skillId}--${examples[0]?.text}`,
          resultValue: getErrorString(e) || e,
          successFlag: false
        });
      }
      throw e;
    }
  };

export const exportIntents = (skillId, intents, invert, all) => ({
  type: actionTypes.EXPORT_INTENTS,
  skillId,
  intents,
  invert,
  all
});

export const importIntentsStart = (skillId, file) => ({
  type: actionTypes.IMPORT_INTENTS_START,
  skillId,
  file
});

export const importIntentsSuccess = response => ({
  type: actionTypes.IMPORT_INTENTS_SUCCESS,
  response,
  payload: { operation: operations.LIST }
});

export const importIntentsError = error => ({
  type: actionTypes.IMPORT_INTENTS_ERROR,
  error
});

export const importIntents = (skillId, file) => async (dispatch, getState) => {
  dispatch(importIntentsStart(skillId, file));
  try {
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to import/listIntents
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    let jobLocation = await intentsApi.importIntents(
      skillId,
      useNewIAUpdatedAccess,
      assistantId,
      file
    );
    let jobInfo,
      counter = 0;
    // should remove `/instances/:instanceId` from the url
    // because we don't want to apply access control if the access control is not on
    // in one cloud url world, there will always be `/instances/:instanceId`
    // we have to remove it conditionally
    // for example, `/instances/5522ca66-8b4d-4438-9afa-dc47089fddda/v2/skills` -> `/v2/skills`
    // if we need the uoa endpoint, we will add it back on server side.
    // eslint-disable-next-line no-useless-escape
    jobLocation = jobLocation.replace(/\/instances\/[^\/]+\//i, '/');
    const throttledRequest = _.throttle(
      (resolve, reject) =>
        intentsApi
          .getImportIntentsJobInfo(jobLocation)
          .then(resolve)
          .catch(reject),
      1000
    ); // throttle the call for 1 second so that it is at most called once per second
    do {
      jobInfo = await new Promise((resolve, reject) =>
        throttledRequest(resolve, reject)
      );
      counter++;
    } while (!jobInfo.finished && counter < 60);
    if (!jobInfo.finished && counter >= 60) {
      throw new Error('Import taking too long.');
    }

    const response = await intentsApi.listIntents(
      skillId,
      useNewIAUpdatedAccess,
      assistantId,
      {},
      null
    );
    const normalizedResult = normalize(response.intents, intentsSchema);
    dispatch(importIntentsSuccess(normalizedResult));
    return {
      entities: normalizedResult,
      nextURL: response.nextURL,
      total: response.total,
      counts: _.get(jobInfo, 'data', {})
    };
  } catch (error) {
    dispatch(importIntentsError(error));
    throw error;
  }
};

export const moveExamplesStart = (skillId, destIntent, values) => ({
  type: actionTypes.MOVE_INTENT_EXAMPLES_START,
  skillId,
  destIntent,
  values
});

export const moveExamplesSuccess = (
  response,
  changedIntents,
  editedIntentName
) => ({
  type: actionTypes.MOVE_INTENT_EXAMPLES_SUCCESS,
  response,
  changedIntents,
  editedIntentName,
  payload: { operation: operations.UPDATE }
});

export const moveExamplesError = error => ({
  type: actionTypes.MOVE_INTENT_EXAMPLES_ERROR,
  transactionId: error.transactionId,
  error
});

export const moveExamples =
  (skillId: string, destIntent: any, values: any, editedIntentName: string) =>
  async (dispatch, getState) => {
    dispatch(moveExamplesStart(skillId, destIntent, values));
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on calls
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    const intentsToRefresh = [destIntent];
    values.forEach(move => {
      if (intentsToRefresh.indexOf(move.source_intent) === -1) {
        intentsToRefresh.push(move.source_intent);
      }
    });
    try {
      await exampleApi.moveExamples(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        destIntent,
        values
      );
      const promises = [];
      intentsToRefresh.forEach(intent => {
        promises.push(
          intentsApi.getIntent(
            skillId,
            useNewIAUpdatedAccess,
            assistantId,
            intent
          )
        );
      });
      return Promise.all(promises).then(results => {
        const normalizedResult = normalize(results, intentsSchema);
        dispatch(
          moveExamplesSuccess(normalizedResult, results, editedIntentName)
        );
        return results;
      });
    } catch (e) {
      dispatch(moveExamplesError(e));
      throw e;
    }
  };

export const listIntentExamplesStart = (skillId, intentName) => ({
  type: actionTypes.LIST_INTENT_EXAMPLES_START,
  skillId,
  intentName
});

export const listIntentExamplesSuccess = response => ({
  type: actionTypes.LIST_INTENT_EXAMPLES_SUCCESS,
  examples: response.examples,
  payload: { operation: operations.LIST }
});

export const listIntentExamplesError = error => ({
  type: actionTypes.LIST_INTENT_EXAMPLES_ERROR,
  error
});

export const createCounterExampleStart = (workspaceId, example, skillId) => ({
  type: actionTypes.CREATE_COUNTEREXAMPLE_START,
  workspaceId,
  example,
  skillId
});

export const createCounterExampleSuccess = response => ({
  type: actionTypes.CREATE_COUNTEREXAMPLE_SUCCESS,
  response,
  payload: { operation: operations.CREATE }
});

export const createCounterExampleError = error => ({
  type: actionTypes.CREATE_COUNTEREXAMPLE_ERROR,
  transactionId: error.transactionId,
  error
});

export const createCounterExample =
  (workspaceId, example, skillId, handleErrors?) =>
  async (dispatch, getState) => {
    dispatch(createCounterExampleStart(workspaceId, example, skillId));
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to createCounterExample
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    try {
      const newCounter = await counterExampleApi.createCounterExample(
        example,
        skillId,
        useNewIAUpdatedAccess,
        assistantId
      );
      dispatch(createCounterExampleSuccess(newCounter));
      // segment event? none in old model code
      return newCounter;
    } catch (e) {
      dispatch(createCounterExampleError(e));
      if (handleErrors) {
        dispatch(messageActions.addErrorMessage(e));
      }
      throw e;
    }
  };

export const removeCounterExampleStart = (workspaceId, example, skillId) => ({
  type: actionTypes.REMOVE_COUNTEREXAMPLE_START,
  workspaceId,
  example,
  skillId
});

export const removeCounterExampleSuccess = response => ({
  type: actionTypes.REMOVE_COUNTEREXAMPLE_SUCCESS,
  response,
  payload: { operation: operations.DELETE }
});

export const removeCounterExampleError = error => ({
  type: actionTypes.REMOVE_COUNTEREXAMPLE_ERROR,
  error
});

export const removeCounterExample =
  (workspaceId, example, skillId) => async (dispatch, getState) => {
    dispatch(removeCounterExampleStart(workspaceId, example, skillId));
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to deleteCounterEx
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    try {
      const removeResult = await counterExampleApi.deleteCounterExample(
        example,
        skillId,
        useNewIAUpdatedAccess,
        assistantId
      );
      dispatch(removeCounterExampleSuccess(removeResult));
      return removeResult;
    } catch (e) {
      dispatch(removeCounterExampleError(e));
      throw e;
    }
  };

export const clearAllIds = () => ({
  type: actionTypes.CLEAR_ALL_IDS
});
